import type { CustomFile, TypeUploadBody } from '../types';

import type { TypeUploadConfig } from './types';
import type { TypeUploadPart } from './core/types';
import { EmptyUploadError } from './errors';

import getBodySize from '../utils/getBodySize';

// ----------------------------------------------------------------------------

export type TypePreparedRequest = {
    body: TypeUploadBody;
    expectedBytes: number;
};

// ----------------------------------------------------------------------------

// The default single-file envelope: one multipart field carrying the blob.
function buildSingleBody(blob: Blob, filename: string, config: TypeUploadConfig): FormData {
    const form = new FormData();

    form.append(config.fieldName ?? 'file', blob, filename);

    return form;
}

// ----------------------------------------------------------------------------

/*
 * Turns one file into the request body — `fnPrepareFile` first, then `fnBody`, falling back
 * to a single-field FormData — and reports the byte count progress is measured against.
 */
async function prepareUploadRequest(
    file: CustomFile,
    config: TypeUploadConfig,
    part?: TypeUploadPart,
): Promise<TypePreparedRequest> {
    /*
     * A chunked attempt carries its slice. `fnPrepareFile` is deliberately skipped here: the chunk
     * feature already applied it to the whole file before slicing, and running it again per slice
     * would transform the same bytes twice.
     */
    if (part) {
        const body = config.chunk?.fnChunkBody
            ? await config.chunk.fnChunkBody(part, file)
            : buildSingleBody(part.blob, file.name, config);

        return { body, expectedBytes: part.blob.size };
    }

    let preparedFile: Blob = file;
    let body: TypeUploadBody;

    // If using function prepare file
    if (typeof config.fnPrepareFile === 'function') {
        const prepared = await config.fnPrepareFile(file);

        if (prepared.size === 0) {
            throw new EmptyUploadError(`Prepared file "${file.name}" is empty`);
        }

        preparedFile = prepared;
    }

    // If using function body
    if (typeof config.fnBody === 'function') {
        body = await config.fnBody(preparedFile);

        const bodySize = getBodySize(body);

        if (!body || (!(body instanceof FormData) && bodySize === 0)) {
            throw new EmptyUploadError(`Body for file "${file.name}" is empty`);
        }

        // FormData carries no measurable size: fall back to the prepared blob so the file
        // still contributes to byte progress instead of being dropped from the total.
        return { body, expectedBytes: bodySize || preparedFile.size };
    }

    const filename = preparedFile instanceof File ? preparedFile.name : file.name;

    return { body: buildSingleBody(preparedFile, filename, config), expectedBytes: preparedFile.size };
}

// ----------------------------------------------------------------------------

export default prepareUploadRequest;
